import React, { useState, useEffect } from "react";
import {
  getRunbook, getRunbookState, updateRunbookTask, updateRunbookGate,
  getMigrationSites, runbookPrefill,
} from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { PrintButton } from "../components/PrintButton";
import { RunbookIntake } from "../components/RunbookIntake";
import { RunbookGuide } from "../components/RunbookGuide";
import { DeviceInventory } from "../components/DeviceInventory";
import { TaskChecks } from "../components/TaskChecks";
import {
  Route, ChevronRight, ChevronDown, Lock, Unlock, ShieldCheck, AlertTriangle,
  CheckCircle2, CircleDashed, Ban, Sparkles, Wand2,
} from "lucide-react";

const STATUS = {
  NOT_STARTED: { label: "Not Started", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  IN_PROGRESS: { label: "In Progress", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  BLOCKED: { label: "Blocked", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  PASSED: { label: "Passed", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  FAILED: { label: "Failed", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  EXCEPTION_ACCEPTED: { label: "Exception", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  ROLLED_BACK: { label: "Rolled Back", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  COMPLETE: { label: "Complete", cls: "bg-emerald-600/15 text-emerald-300 border-emerald-600/30" },
};
const STATUSES = Object.keys(STATUS);
const PASSABLE = new Set(["PASSED", "COMPLETE", "EXCEPTION_ACCEPTED"]);
const FIELDS = [
  ["owner", "Owner"], ["priority", "Priority"], ["dependency", "Dependency"],
  ["cisco_ref", "Cisco Reference"], ["evidence", "Required Evidence"], ["validation", "Validation Procedure"],
  ["expected", "Expected Result"], ["actual", "Actual Result"], ["exception", "Exception"],
  ["remediation", "Remediation"], ["rollback", "Rollback Action"], ["completion_time", "Completion Time"],
  ["engineer_signoff", "Engineer Sign-Off"], ["business_signoff", "Business Sign-Off"],
];

export default function Runbook() {
  const [rb, setRb] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [track, setTrack] = useState("webex");
  const [sites, setSites] = useState([]);
  const [selId, setSelId] = useState("");
  const [tasks, setTasks] = useState({});
  const [gates, setGates] = useState({});
  const [intake, setIntake] = useState({});
  const [complete, setComplete] = useState(false);
  const [openPhase, setOpenPhase] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideSeed, setGuideSeed] = useState(null);
  const [activeTask, setActiveTask] = useState(null);

  useEffect(() => {
    getMigrationSites().then((d) => { setSites(d); if (d[0]) setSelId(d[0].id); }).catch(() => {});
  }, []);

  // (Re)load the runbook definition whenever the selected track changes.
  useEffect(() => {
    getRunbook(track).then((d) => {
      setRb(d);
      if (d.tracks) setTracks(d.tracks);
      setOpenPhase(d.phases?.[0]?.id || null);
    }).catch(() => {});
    if (selId) refreshState(selId, track);
    /* eslint-disable-next-line */
  }, [track]);

  useEffect(() => { if (selId) refreshState(selId, track); /* eslint-disable-next-line */ }, [selId]);

  const refreshState = (siteId, tk = track) =>
    getRunbookState(siteId, tk).then((s) => {
      setTasks(s.tasks || {}); setGates(s.gates || {});
      setIntake(s.intake || {}); setComplete(s.complete);
    }).catch(() => {});

  const site = sites.find((s) => s.id === selId);

  const setTaskField = async (taskId, field, value) => {
    setTasks((p) => ({ ...p, [taskId]: { ...(p[taskId] || {}), [field]: value } }));
    try { await updateRunbookTask(taskId, selId, { [field]: value }, track); }
    catch { toast.error("Save failed"); }
  };

  const phaseProgress = (phase) => {
    const ids = phase.task_ids;
    const done = ids.filter((id) => PASSABLE.has((tasks[id] || {}).status)).length;
    return { done, total: ids.length, pct: ids.length ? Math.round((done / ids.length) * 100) : 0 };
  };

  const setGonogo = async (itemId, val) => {
    const gp = rb?.phases?.find((p) => p.is_gonogo)?.id;
    if (!gp) return;
    setGates((p) => ({ ...p, [gp]: { ...(p[gp] || {}), gonogo: { ...((p[gp] || {}).gonogo || {}), [itemId]: val } } }));
    try { await updateRunbookGate(gp, selId, { gonogo: { [itemId]: val } }, track); }
    catch { toast.error("Save failed"); }
  };

  const approveGate = async (phase) => {
    const approver = window.prompt(`Approver name to sign off the "${phase.name}" gate:`, "");
    if (approver === null) return;
    try {
      const g = await updateRunbookGate(phase.id, selId, { approved: true, approver }, track);
      setGates((p) => ({ ...p, [phase.id]: g }));
      refreshState(selId, track);
      toast.success(`${phase.name} gate approved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gate blocked");
    }
  };
  const reopenGate = async (phase) => {
    try {
      const g = await updateRunbookGate(phase.id, selId, { approved: false }, track);
      setGates((p) => ({ ...p, [phase.id]: g }));
      refreshState(selId, track);
      toast.info(`${phase.name} gate reopened`);
    } catch { toast.error("Failed"); }
  };

  const prefill = async () => {
    setPrefilling(true);
    try {
      const res = await runbookPrefill(selId, track);
      await refreshState(selId, track);
      if (res.count) toast.success(`${res.count} task(s) auto-filled from your trackers`);
      else toast.info("No matching discovery data found for this site yet");
    } catch { toast.error("Prefill failed"); }
    setPrefilling(false);
  };

  const askGuide = (phaseName, task) => {
    setActiveTask({ id: task.id, title: task.title });
    setGuideSeed({
      message: `For the "${task.title}" task in the ${phaseName} phase — what exactly do I check or do, where do I run it (CUCM CLI / RTMT / CUBE / Webex Control Hub), and what does good output look like?`,
      phase_name: phaseName, task_title: task.title,
    });
    setGuideOpen(true);
  };

  // Append an AI-suggested command to the active task's evidence field (one-click).
  const appendEvidence = async (taskId, text) => {
    const cur = (tasks[taskId] || {}).evidence || "";
    const next = cur ? `${cur}\n${text}` : text;
    await setTaskField(taskId, "evidence", next);
    toast.success("Added to task evidence");
  };

  if (!rb) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading runbook…</div>;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> runbook <ChevronRight className="h-3 w-3" /> {rb.title}
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="no-print flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button
            onClick={() => { setGuideSeed(null); setActiveTask(null); setGuideOpen(true); }}
            data-testid="open-guide-btn"
            className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <Sparkles className="h-4 w-4" /> AI Migration Guide
          </button>
          <PrintButton label="Print Runbook" />
        </div>

        {/* Track selector — pick the engineering project */}
        {tracks.length > 1 && (
          <div className="no-print inline-flex items-center gap-1 p-1 rounded-lg border border-zinc-800 bg-zinc-900 mb-5" data-testid="runbook-track-switch">
            {tracks.map((t) => (
              <button
                key={t.track}
                onClick={() => { setTrack(t.track); }}
                data-testid={`track-${t.track}`}
                className={`text-[12px] font-semibold px-3.5 py-1.5 rounded-md transition-colors ${track === t.track ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Route className="h-4 w-4" /> {rb.title} · {rb.subtitle}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2" data-testid="runbook-title">{rb.title}</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          A gated engineering change per location: {rb.phases.map((p) => p.name).join(" → ")}.
          Each site runs its own independent runbook so waves progress separately.
        </p>

        {/* Per-site selector */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1.5">Location</label>
            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger data-testid="runbook-site-select" className="w-64 h-10 bg-zinc-900 border-zinc-800 text-sm">
                <SelectValue placeholder="Select a site" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3" data-testid="runbook-status">
            <span className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border ${complete ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/20"}`}>
              {complete ? "COMPLETE" : "In Progress"}
            </span>
            <span className="text-[12px] text-zinc-500">
              {rb.phases.filter((p) => gates[p.id]?.approved).length}/{rb.phases.length} gates approved
            </span>
          </div>
        </div>

        {!selId ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Select a location to begin…</div>
        ) : (
          <>
            {/* Discovery questionnaire */}
            <div className="mt-6">
              <RunbookIntake siteId={selId} schema={rb.questionnaire || []} answers={intake} setAnswers={setIntake} track={track} />
            </div>

            {/* Device compatibility — readiness gate that blocks cutover */}
            <div className="mt-4">
              <DeviceInventory siteId={selId} track={track} />
            </div>

            {/* Phases */}
            <div className="mt-2 space-y-3">
              {rb.phases.map((phase, i) => {
                const prev = i > 0 ? rb.phases[i - 1] : null;
                const locked = prev && !gates[prev.id]?.approved;
                const gate = gates[phase.id] || {};
                const prog = phaseProgress(phase);
                const open = openPhase === phase.id;
                const gateReady = prog.done === prog.total && (!phase.is_gonogo || rb.gonogo.every((g) => (gate.gonogo || {})[g.id]));
                return (
                  <div key={phase.id} data-testid={`phase-${phase.id}`} className={`rounded-lg border bg-zinc-900 overflow-hidden ${gate.approved ? "border-emerald-600/30" : locked ? "border-zinc-800 opacity-70" : "border-zinc-800"}`}>
                    <button onClick={() => setOpenPhase(open ? null : phase.id)} data-testid={`phase-toggle-${phase.id}`} className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
                      <span className="h-7 w-7 rounded-full bg-zinc-950 border border-zinc-700 flex items-center justify-center text-[12px] font-bold text-zinc-400 shrink-0">{phase.order}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{phase.name}</span>
                          {locked ? <Lock className="h-3.5 w-3.5 text-zinc-500" /> : gate.approved ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> : <Unlock className="h-3.5 w-3.5 text-blue-400" />}
                        </div>
                        <div className="text-[11px] text-zinc-500">{phase.subtitle}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`text-[13px] font-mono ${prog.pct === 100 ? "text-emerald-400" : "text-zinc-400"}`}>{prog.done}/{prog.total}</div>
                        <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{gate.approved ? "Gate ✓" : "tasks"}</div>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    <div className="h-1 bg-zinc-800"><div className={`h-full ${gate.approved ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${prog.pct}%` }} /></div>

                    {open && (
                      <div className="p-4 border-t border-zinc-800">
                        {locked && (
                          <div data-testid={`phase-locked-${phase.id}`} className="mb-4 flex items-center gap-2 text-[13px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                            <Lock className="h-4 w-4" /> Locked — approve the "{prev.name}" gate before working this phase.
                          </div>
                        )}

                        {/* Prefill from Discovery (first phase only) */}
                        {phase.is_prefill && !locked && (
                          <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-blue-600/25 bg-blue-600/5 px-3 py-2.5">
                            <div className="text-[12px] text-zinc-300 flex items-center gap-2">
                              <Wand2 className="h-4 w-4 text-blue-400 shrink-0" />
                              Auto-tick inventory tasks using data already captured in your E911, Rooms & site trackers.
                            </div>
                            <button onClick={prefill} disabled={prefilling} data-testid="prefill-btn"
                              className="no-print shrink-0 text-[11px] px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-medium">
                              {prefilling ? "Filling…" : "Prefill from Discovery"}
                            </button>
                          </div>
                        )}

                        {phase.groups.map((grp) => (
                          <div key={grp.name} className="mb-4">
                            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">{grp.name}</div>
                            <div className="space-y-1.5">
                              {grp.tasks.map((t) => {
                                const st = tasks[t.id] || {};
                                const status = st.status || "NOT_STARTED";
                                const topen = openTask === t.id;
                                return (
                                  <div key={t.id} data-testid={`task-${t.id}`} className="rounded-md border border-zinc-800 bg-zinc-950">
                                    <div className="flex items-center gap-2 px-3 py-2">
                                      <button onClick={() => setOpenTask(topen ? null : t.id)} className="flex-1 flex items-center gap-2 text-left min-w-0" data-testid={`task-toggle-${t.id}`}>
                                        {PASSABLE.has(status) ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : status === "FAILED" ? <Ban className="h-3.5 w-3.5 text-red-400 shrink-0" /> : <CircleDashed className="h-3.5 w-3.5 text-zinc-600 shrink-0" />}
                                        <span className="text-[13px] text-zinc-200 truncate">{t.title}</span>
                                        {t.checks && <span className="text-[9px] uppercase tracking-wider text-emerald-500/70 border border-emerald-500/30 rounded px-1 shrink-0">how-to</span>}
                                      </button>
                                      <Select value={status} onValueChange={(v) => setTaskField(t.id, "status", v)} disabled={locked}>
                                        <SelectTrigger data-testid={`task-status-${t.id}`} className="h-7 w-36 bg-zinc-900 border-zinc-800 text-[11px] shrink-0"><SelectValue /></SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {topen && !locked && (
                                      <div className="px-3 pb-3 pt-1 border-t border-zinc-800" data-testid={`task-detail-${t.id}`}>
                                        <div className="grid sm:grid-cols-2 gap-2">
                                          <TaskChecks checks={t.checks} testId={`task-checks-${t.id}`} />
                                          {FIELDS.map(([f, label]) => (
                                            <div key={f}>
                                              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</label>
                                              <Input defaultValue={st[f] || ""} onBlur={(e) => { if (e.target.value !== (st[f] || "")) setTaskField(t.id, f, e.target.value); }} className="h-8 bg-zinc-900 border-zinc-800 text-[12px] mt-0.5" />
                                            </div>
                                          ))}
                                        </div>
                                        <button onClick={() => askGuide(phase.name, t)} data-testid={`ask-guide-${t.id}`}
                                          className="no-print mt-3 flex items-center gap-1.5 text-[11px] font-medium text-blue-400 hover:text-blue-300">
                                          <Sparkles className="h-3.5 w-3.5" /> Ask AI: how & where do I do this?
                                        </button>
                                      </div>
                                    )}
                                    {/* print-only field summary */}
                                    <div className="print-show px-3 pb-2 text-[11px] text-zinc-500">
                                      Status: {STATUS[status]?.label}{st.owner ? ` · Owner: ${st.owner}` : ""}{st.actual ? ` · Actual: ${st.actual}` : ""}{st.exception ? ` · Exception: ${st.exception}` : ""}{st.engineer_signoff ? ` · Eng: ${st.engineer_signoff}` : ""}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        {/* GO / NO-GO for the execution phase */}
                        {phase.is_gonogo && !locked && (
                          <div className="mb-4" data-testid="gonogo">
                            <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold mb-2">GO / NO-GO Confirmation</div>
                            <div className="grid sm:grid-cols-2 gap-1.5">
                              {rb.gonogo.map((g) => {
                                const yes = (gate.gonogo || {})[g.id];
                                return (
                                  <div key={g.id} className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5">
                                    <span className="text-[12px] text-zinc-300 truncate">{g.label}</span>
                                    <div className="flex gap-1 shrink-0">
                                      <button onClick={() => setGonogo(g.id, true)} data-testid={`gonogo-${g.id}-yes`} className={`text-[10px] uppercase font-semibold px-2 py-1 rounded border ${yes === true ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-700"}`}>Yes</button>
                                      <button onClick={() => setGonogo(g.id, false)} data-testid={`gonogo-${g.id}-no`} className={`text-[10px] uppercase font-semibold px-2 py-1 rounded border ${yes === false ? "bg-red-500/20 text-red-400 border-red-500/40" : "text-zinc-500 border-zinc-700"}`}>No</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Critical safeguard: a critical smoke-test failure must be resolved via REMEDIATE → ACCEPT DOCUMENTED EXCEPTION → ROLLBACK. Cutover cannot be marked complete on an unresolved critical failure.
                            </div>
                          </div>
                        )}

                        {/* Gate */}
                        {!locked && (
                          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3" data-testid={`gate-${phase.id}`}>
                            <div className="flex items-start gap-2">
                              <ShieldCheck className={`h-4 w-4 mt-0.5 shrink-0 ${gate.approved ? "text-emerald-400" : "text-zinc-500"}`} />
                              <div className="flex-1">
                                <div className="text-[12px] text-zinc-300">{phase.gate}</div>
                                {gate.approved ? (
                                  <div className="text-[12px] text-emerald-400 mt-1">Approved by {gate.approver || "—"} · {gate.approved_at ? new Date(gate.approved_at).toLocaleString() : ""}</div>
                                ) : (
                                  <div className="text-[12px] text-zinc-500 mt-1">{prog.done}/{prog.total} tasks passable{phase.is_gonogo ? " · GO/NO-GO must be unanimous" : ""}</div>
                                )}
                              </div>
                              {gate.approved ? (
                                <button onClick={() => reopenGate(phase)} data-testid={`reopen-gate-${phase.id}`} className="no-print text-[11px] px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Reopen</button>
                              ) : (
                                <button onClick={() => approveGate(phase)} disabled={!gateReady} data-testid={`approve-gate-${phase.id}`} className="no-print text-[11px] px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium">Approve gate</button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <RunbookGuide
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        siteId={selId}
        siteName={site?.name}
        seed={guideSeed}
        onSeedConsumed={() => setGuideSeed(null)}
        activeTask={activeTask}
        onAddCommand={appendEvidence}
      />
    </div>
  );
}
